import React from 'react'
import { createRoot } from 'react-dom/client'
import type { AppDispatch } from './store'
import type { ClientConfigRuntime } from './utils/client-config-runtime'
import type { SentryConfigFromStore } from './utils/sentry-init'
import { logger } from './utils/logger'

// Avoid "Uncaught (in promise)" when Vite HMR fails (e.g. app loaded via tunnel but HOST was unset, so client tries ws://localhost:64999).
window.addEventListener('unhandledrejection', (event) => {
  const message = event.reason?.message ?? String(event.reason)
  if (
    typeof message === 'string' &&
    (message.includes('WebSocket closed without opened') ||
      message.includes('failed to connect to websocket'))
  ) {
    logger.debug(
      '[App] HMR WebSocket failed (expected when using tunnel without HOST set):',
      message,
    )
    event.preventDefault()
  }
})

function deferSentryInit({ config }: { config: SentryConfigFromStore }): void {
  const schedule =
    typeof requestAnimationFrame === 'function'
      ? requestAnimationFrame
      : (callback: FrameRequestCallback) => {
          setTimeout(() => {
            callback(Date.now())
          }, 0)
        }

  schedule(() => {
    void import('./utils/sentry-init').then(({ initSentryFromConfig }) => {
      try {
        initSentryFromConfig({ config })
      } catch (_err) {
        // Continue without Sentry
      }
    })
  })
}

/**
 * Initializes the app: i18n + client config in parallel, renders the shell immediately,
 * then applies runtime config and deferred Sentry after the config fetch settles.
 * @throws Re-throws initialization errors after rendering an error UI
 */
export async function main() {
  try {
    logger.debug('[App] Starting initialization...')
    const { initI18n } = await import('./utils/i18n-utils')
    const { createStore } = await import('./store')
    const { default: App } = await import('./app')

    const store = createStore()
    logger.debug('[App] Starting i18n and admin shell fetches in parallel...')
    const i18nPromise = initI18n()
    const { startAdminBoot } = await import('./store/start-admin-boot')
    const { configRequest } = startAdminBoot({
      dispatch: store.dispatch as AppDispatch,
      getState: () => store.getState(),
    })

    const rootElement = document.getElementById('app')
    if (!rootElement) {
      throw new Error('Root element id="app" not found')
    }

    await i18nPromise

    logger.debug('[App] Rendering app shell before config settles...')
    const root = createRoot(rootElement)
    root.render(<App />)

    if (configRequest) {
      await configRequest
    }

    const { config } = store.getState().clientConfig
    if (config) {
      const { setClientConfigRuntime } =
        await import('./utils/client-config-runtime')
      setClientConfigRuntime(config as unknown as ClientConfigRuntime)
      deferSentryInit({ config: config as SentryConfigFromStore })
      void import('./utils/posthog-init').then(({ initPosthogFromConfig }) => {
        try {
          initPosthogFromConfig({ config })
        } catch (_err) {
          // Continue without PostHog
        }
      })
    }

    logger.debug('[App] App rendered successfully')
  } catch (error) {
    logger.error('[App] Failed to initialize:', error)
    const rootElement = document.getElementById('app')
    if (rootElement) {
      const root = createRoot(rootElement)
      root.render(
        <div style={{ padding: '20px', fontFamily: 'system-ui' }}>
          <h1>Application Error</h1>
          <p>Failed to initialize the application.</p>
          <pre
            style={{
              background: '#f5f5f5',
              padding: '10px',
              borderRadius: '4px',
              overflow: 'auto',
            }}
          >
            {error instanceof Error ? error.message : String(error)}
            {error instanceof Error && error.stack ? `\n\n${error.stack}` : ''}
          </pre>
        </div>,
      )
    }
    throw error
  }
}

/** Max time to wait for window.shopify when embedded (avoids indefinite hang on reload race). */
const SHOPIFY_READY_TIMEOUT_MS = 5000
/** Interval for polling window.shopify when ready promise is not available. */
const SHOPIFY_POLL_INTERVAL_MS = 50

/**
 * Detects if the app is running inside the Shopify admin iframe (embedded context).
 * Uses URL params: host or embedded=1, which Shopify appends when loading the app in admin.
 */
function isEmbedded(): boolean {
  if (typeof window === 'undefined') return false
  const params = new URLSearchParams(window.location.search)
  return params.has('host') || params.get('embedded') === '1'
}

/**
 * Waits for window.shopify (App Bridge) to be ready. Uses shopify.ready promise when
 * available; otherwise polls. Resolves after timeout even if shopify never appears.
 */
function waitForShopify(): Promise<void> {
  return new Promise((resolve) => {
    const w = window as Window & { shopify?: { ready?: Promise<unknown> } }
    if (w.shopify) {
      if (
        typeof w.shopify.ready === 'object' &&
        w.shopify.ready instanceof Promise
      ) {
        w.shopify.ready.then(() => resolve()).catch(() => resolve())
        return
      }
      resolve()
      return
    }
    const deadline = Date.now() + SHOPIFY_READY_TIMEOUT_MS
    const poll = () => {
      if (w.shopify) {
        resolve()
        return
      }
      if (Date.now() >= deadline) {
        logger.warn(
          '[App] window.shopify not available after timeout; proceeding anyway',
        )
        resolve()
        return
      }
      setTimeout(poll, SHOPIFY_POLL_INTERVAL_MS)
    }
    poll()
  })
}

/**
 * Starts downloading boot modules. Does not fetch admin data or render.
 * Shell fetches stay in `main()`, after App Bridge is ready when embedded.
 */
function startBootstrapModuleLoads(): void {
  void import('./utils/i18n-utils')
  void import('./store')
  void import('./app')
  const pathname = window.location.pathname
  void import('./routes/prefetch-entry-route').then(
    ({ prefetchEntryRoute }) => {
      prefetchEntryRoute({ pathname })
    },
  )
}

/**
 * Bootstrap entry point. When embedded, waits for App Bridge (window.shopify) before
 * initializing the app, avoiding the reload race where shopify is undefined.
 * Module downloads overlap that wait. Admin fetches do not.
 */
async function bootstrap(): Promise<void> {
  if (isEmbedded()) {
    const { persistEmbeddedAppParamsFromUrl } =
      await import('./utils/embedded-app-params')
    persistEmbeddedAppParamsFromUrl()
    startBootstrapModuleLoads()
    await waitForShopify()
  } else {
    startBootstrapModuleLoads()
  }
  await main()
}

bootstrap().catch((error) => {
  logger.error('[App] Unhandled error in bootstrap():', error)
})
